import React, { useState, useEffect } from "react";
import * as Icons from "lucide-react";
import {
  Heart, HeartHandshake, Church, MapPin, Clock, Phone, Mail, Navigation,
  CalendarDays, Check, Loader2, Sparkles, X, ShieldCheck, HandHeart, PartyPopper,
} from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { UI, t } from "@/i18n/translations";
import { api } from "@/lib/api";
import { CHURCH, OFFERINGS, EVENTS } from "@/data/community";

const addressDirections = () =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(CHURCH.address)}`;

export default function Communaute() {
  const { lang } = useLang();
  const { user, setUser } = useAuth();
  const C = UI.communityMod;

  const [status, setStatus] = useState(null);
  const [showJoin, setShowJoin] = useState(false);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rsvps, setRsvps] = useState([]);
  const [prayer, setPrayer] = useState("");
  const [showPrayer, setShowPrayer] = useState(false);

  useEffect(() => {
    api.get("/community/status").then(({ data }) => setStatus(data)).catch(() => {});
  }, []);

  // Keep local membership in sync if it changes elsewhere (e.g. left via profile)
  useEffect(() => {
    setStatus((s) => (s ? { ...s, is_member: !!user?.community_member } : s));
  }, [user?.community_member]);

  const answerFaith = async (answer) => {
    setStatus((s) => ({ ...s, faith_answered: true, faith_answer: answer }));
    try { await api.post("/community/faith", { answer }); } catch (err) { console.error("Failed to save faith answer:", err); }
  };

  const join = async () => {
    if (!consent) return;
    setBusy(true);
    try {
      await api.post("/community/join", { consent: true });
      setStatus((s) => ({ ...s, is_member: true }));
      if (user) setUser({ ...user, community_member: true });
      setShowJoin(false);
      setConsent(false);
      toast.success(t(C.joinedTitle, lang));
    } catch (e) {
      toast.error(e.response?.data?.detail || UI.common.error[lang]);
    } finally {
      setBusy(false);
    }
  };

  const rsvp = async (ev) => {
    setRsvps((r) => [...r, ev.id]);
    try {
      await api.post("/community/rsvp", { event_id: ev.id, event_title: t(ev.title, lang) });
      toast.success(t(C.rsvpSent, lang));
    } catch {
      setRsvps((r) => r.filter((x) => x !== ev.id));
      toast.error(UI.common.error[lang]);
    }
  };

  const accompaniment = async () => {
    try {
      await api.post("/community/accompaniment", {});
      toast.success(t(C.accompanySent, lang));
    } catch {
      toast.error(UI.common.error[lang]);
    }
  };

  const sendPrayer = async () => {
    if (!prayer.trim()) return;
    setBusy(true);
    try {
      await api.post("/community/prayer", { message: prayer });
      setPrayer("");
      setShowPrayer(false);
      toast.success(t(C.prayerSent, lang));
    } catch {
      toast.error(UI.common.error[lang]);
    } finally {
      setBusy(false);
    }
  };

  if (!status) return <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin text-amber" /></div>;

  const showInvitation = status.faith_answer === "no" && !status.is_member;

  return (
    <div className="animate-fade-up space-y-5">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">{t(C.title, lang)}</h1>
        <p className="text-sm text-ink/60 mt-1">{t(C.intro, lang)}</p>
      </div>

      {/* Faith question — asked once */}
      {!status.faith_answered && (
        <div className="rounded-[18px] bg-white border border-line p-5 shadow-sm" data-testid="faith-question">
          <div className="flex items-start gap-3">
            <span className="grid place-items-center w-9 h-9 rounded-xl bg-amber/15 shrink-0">
              <HeartHandshake className="w-5 h-5 text-amber" />
            </span>
            <div>
              <p className="font-semibold text-sm leading-snug">{t(C.faithQuestion, lang)}</p>
              <p className="text-xs text-ink/50 mt-1">{t(C.faithHint, lang)}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <button data-testid="faith-yes" onClick={() => answerFaith("yes")} className="rounded-full border border-line bg-paper px-4 py-2 text-sm font-semibold hover:border-sage transition-colors">{t(C.yes, lang)}</button>
            <button data-testid="faith-no" onClick={() => answerFaith("no")} className="rounded-full border border-line bg-paper px-4 py-2 text-sm font-semibold hover:border-amber transition-colors">{t(C.no, lang)}</button>
            <button data-testid="faith-prefer" onClick={() => answerFaith("prefer_not")} className="rounded-full border border-line bg-paper px-4 py-2 text-sm font-semibold hover:border-line transition-colors">{t(C.preferNot, lang)}</button>
          </div>
        </div>
      )}

      {/* Member banner */}
      {status.is_member && (
        <div className="rounded-[18px] bg-sage/12 border border-sage/30 p-5" data-testid="member-banner">
          <div className="flex items-center gap-2 text-sage font-bold">
            <PartyPopper className="w-5 h-5" /> {t(C.joinedTitle, lang)}
          </div>
          <p className="text-sm text-ink/70 mt-2">{t(C.joinedBody, lang)}</p>
          <ul className="text-sm text-ink/70 mt-2 space-y-1.5">
            <li className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-sage" /> {t(C.nextEvent, lang)} — {t(EVENTS[0].title, lang)} ({t(EVENTS[0].when, lang)})</li>
            <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-sage" /> {CHURCH.email}</li>
            <li className="flex items-center gap-2"><Icons.MessageCircle className="w-4 h-4 text-sage" /> {t(C.whatsappNote, lang)}</li>
          </ul>
        </div>
      )}

      {/* Welcome word */}
      <div className="rounded-[18px] bg-ink text-paper p-6">
        <div className="flex items-center gap-2 mb-2">
          <Church className="w-5 h-5 text-amber" />
          <h2 className="font-display text-xl font-bold">{t(C.welcomeTitle, lang)}</h2>
        </div>
        <p className="text-sm text-paper/80 leading-relaxed">{t(C.welcomeBody, lang)}</p>
      </div>

      {/* Invitation (only if 'no' & not member) */}
      {showInvitation && (
        <div className="rounded-[18px] bg-amber/12 border border-amber/40 p-6" data-testid="invitation-card">
          <div className="flex items-center gap-2 mb-2">
            <Heart className="w-5 h-5 text-amber" />
            <h2 className="font-display text-lg font-bold">{t(C.invitationTitle, lang)}</h2>
          </div>
          <p className="text-sm text-ink/75 leading-relaxed">{t(C.invitationBody, lang)}</p>
          <button
            data-testid="open-join-btn-invitation"
            onClick={() => setShowJoin(true)}
            className="w-full mt-4 rounded-2xl bg-amber text-ink font-bold py-3.5 flex items-center justify-center gap-2 hover:brightness-105 active:scale-[0.99] transition"
          >
            <HandHeart className="w-4 h-4" /> {t(C.joinBtn, lang)}
          </button>
        </div>
      )}

      {/* Offerings */}
      <div className="rounded-[18px] bg-white border border-line p-5 shadow-sm">
        <h2 className="font-display text-lg font-bold flex items-center gap-2 mb-3">
          <Sparkles className="w-5 h-5 text-amber" /> {t(C.offeringsTitle, lang)}
        </h2>
        <ul className="space-y-2">
          {(OFFERINGS[lang] || OFFERINGS.fr).map((o) => (
            <li key={o} className="flex gap-2.5 text-sm text-ink/75 leading-relaxed">
              <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-sage" /> {o}
            </li>
          ))}
        </ul>
      </div>

      {/* Events */}
      <div>
        <h2 className="font-display text-lg font-bold flex items-center gap-2 mb-3">
          <CalendarDays className="w-5 h-5 text-amber" /> {t(C.eventsTitle, lang)}
        </h2>
        <div className="space-y-2.5">
          {EVENTS.map((ev) => {
            const EvIcon = Icons[ev.icon] || CalendarDays;
            const done = rsvps.includes(ev.id);
            return (
              <div key={ev.id} data-testid={`event-${ev.id}`} className="rounded-[18px] bg-white border border-line p-4 flex items-center gap-3">
                <span className="grid place-items-center w-10 h-10 rounded-xl bg-amber/15 shrink-0">
                  <EvIcon className="w-5 h-5 text-amber" strokeWidth={2.2} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{t(ev.title, lang)}</div>
                  <div className="text-xs text-ink/55">{t(ev.when, lang)} · {t(ev.type, lang)}</div>
                </div>
                <button
                  data-testid={`rsvp-${ev.id}`}
                  onClick={() => rsvp(ev)}
                  disabled={done}
                  className={`shrink-0 rounded-full px-3.5 py-2 text-xs font-bold flex items-center gap-1.5 transition ${
                    done ? "bg-sage/15 text-sage" : "bg-amber text-ink hover:brightness-105"
                  }`}
                >
                  {done ? <><Check className="w-3.5 h-3.5" /> {t(C.rsvpDone, lang)}</> : t(C.rsvp, lang)}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Practical info */}
      <div className="rounded-[18px] bg-white border border-line p-5 shadow-sm space-y-3">
        <h2 className="font-display text-lg font-bold">{t(C.practicalTitle, lang)} · {CHURCH.name}</h2>
        <div className="flex items-start gap-3">
          <MapPin className="w-4.5 h-4.5 text-sage shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-xs font-semibold text-ink/50">{t(C.address, lang)}</div>
            <div className="text-sm">{CHURCH.address}</div>
          </div>
          <a
            href={addressDirections()}
            target="_blank"
            rel="noreferrer"
            data-testid="church-directions"
            aria-label={t(C.directions, lang)}
            className="shrink-0 grid place-items-center w-10 h-10 rounded-xl bg-sage/12 text-sage hover:bg-sage hover:text-white transition-colors"
          >
            <Navigation className="w-4.5 h-4.5" strokeWidth={2.2} />
          </a>
        </div>
        <div className="flex items-start gap-3">
          <Clock className="w-4.5 h-4.5 text-sage shrink-0 mt-0.5" />
          <div>
            <div className="text-xs font-semibold text-ink/50">{t(C.services, lang)}</div>
            {(CHURCH.services[lang] || CHURCH.services.fr).map((s) => (
              <div key={s} className="text-sm">{s}</div>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Phone className="w-4.5 h-4.5 text-sage shrink-0" />
          <a href={`tel:${CHURCH.phone.replace(/\s/g, "")}`} className="text-sm">{CHURCH.phone}</a>
        </div>
        <div className="flex items-center gap-3">
          <Mail className="w-4.5 h-4.5 text-sage shrink-0" />
          <a href={`mailto:${CHURCH.email}`} className="text-sm">{CHURCH.email}</a>
        </div>
      </div>

      {/* Accompaniment */}
      <div className="rounded-[18px] bg-white border border-line p-5 shadow-sm">
        <h2 className="font-display text-base font-bold flex items-center gap-2">
          <HeartHandshake className="w-5 h-5 text-amber" /> {t(C.accompanyTitle, lang)}
        </h2>
        <p className="text-sm text-ink/60 mt-1.5">{t(C.accompanyBody, lang)}</p>
        <button
          data-testid="accompaniment-btn"
          onClick={accompaniment}
          className="w-full mt-3 rounded-2xl border border-line bg-paper py-3 text-sm font-semibold hover:border-amber transition-colors"
        >
          {t(C.accompanyBtn, lang)}
        </button>
      </div>

      {/* Prayer / need */}
      <div className="rounded-[18px] bg-white border border-line p-5 shadow-sm">
        <h2 className="font-display text-base font-bold flex items-center gap-2">
          <HandHeart className="w-5 h-5 text-amber" /> {t(C.prayerTitle, lang)}
        </h2>
        <p className="text-sm text-ink/60 mt-1.5">{t(C.prayerHint, lang)}</p>
        {showPrayer ? (
          <div className="mt-3 space-y-2">
            <textarea
              data-testid="prayer-input"
              value={prayer}
              onChange={(e) => setPrayer(e.target.value)}
              placeholder={t(C.prayerPlaceholder, lang)}
              rows={3}
              className="w-full rounded-2xl border border-line bg-paper px-3.5 py-3 text-sm outline-none focus:border-amber resize-none"
            />
            <div className="flex gap-2">
              <button data-testid="prayer-send" onClick={sendPrayer} disabled={busy || !prayer.trim()} className="flex-1 rounded-2xl bg-amber text-ink font-bold py-2.5 text-sm disabled:opacity-60 flex items-center justify-center gap-2">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t(C.prayerSend, lang)}
              </button>
              <button onClick={() => setShowPrayer(false)} className="rounded-2xl border border-line px-4 text-sm font-semibold text-ink/60">{t(C.joinCancel, lang)}</button>
            </div>
          </div>
        ) : (
          <button data-testid="open-prayer-btn" onClick={() => setShowPrayer(true)} className="w-full mt-3 rounded-2xl border border-line bg-paper py-3 text-sm font-semibold hover:border-amber transition-colors">
            {t(C.prayerTitle, lang)}
          </button>
        )}
      </div>

      {/* Join button (when not invitation card & not member) */}
      {!status.is_member && !showInvitation && (
        <button
          data-testid="open-join-btn-inline"
          onClick={() => setShowJoin(true)}
          className="w-full rounded-2xl bg-amber text-ink font-bold py-3.5 flex items-center justify-center gap-2 hover:brightness-105 active:scale-[0.99] transition"
        >
          <HandHeart className="w-4 h-4" /> {t(C.joinBtn, lang)}
        </button>
      )}

      <p className="flex items-start gap-2 text-xs text-ink/50 leading-relaxed">
        <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5 text-sage" />
        {t(C.respectNote, lang)}
      </p>

      {/* Join consent modal */}
      {showJoin && (
        <div className="fixed inset-0 z-50 grid place-items-end sm:place-items-center" data-testid="join-modal">
          <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={() => setShowJoin(false)} />
          <div className="relative w-full max-w-md bg-paper rounded-t-[24px] sm:rounded-[24px] p-6 animate-fade-up">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-xl font-bold">{t(C.joinConsentTitle, lang)}</h3>
              <button onClick={() => setShowJoin(false)} className="text-ink/40"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-ink/60 leading-relaxed mb-4">{t(C.joinConsentNote, lang)}</p>
            <label className="flex items-start gap-3 rounded-2xl border border-line bg-white p-4 cursor-pointer">
              <input
                data-testid="join-consent-checkbox"
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 w-5 h-5 accent-sage shrink-0"
              />
              <span className="text-sm text-ink/80 leading-relaxed">{t(C.joinConsentLabel, lang)}</span>
            </label>
            <div className="flex gap-2 mt-4">
              <button
                data-testid="confirm-join-btn"
                onClick={join}
                disabled={!consent || busy}
                className="flex-1 rounded-2xl bg-amber text-ink font-bold py-3.5 flex items-center justify-center gap-2 disabled:opacity-50 transition"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t(C.joinConfirm, lang)}
              </button>
              <button onClick={() => setShowJoin(false)} className="rounded-2xl border border-line px-5 font-semibold text-sm text-ink/60">{t(C.joinCancel, lang)}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
